import { useMemo, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabList, Tab, TabPanel } from "../../../../../../components/core/basic/Tabs";
import { DatesProvider } from "../../../../../../dates/DatesContext";
import { useDates } from "../../../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../../../i18n/useDateFormatter";
import { useModuleEnabled } from "../../../../../../redux/selectors/modules";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { Col, Row, Table as ReadOnlyTable } from "react-bootstrap";
import useDSPCreativeData from "../../../../../../modules/dsp/hooks/useDSPCreativeData";
import BreadcrumbDates from "../../../../../../components/core/blocks/BreadcrumbDates";
import { DASHBOARD_LINKS } from "../../../../../../utils/dashboardLinks";
import MetricsCard from "../../../../../../components/custom/merchandise/MetricsCard";
import getMetricSpec from "../../../../../../modules/merchandising/utils/metrics";
import { useDSPLineItemDatasetsQuery } from "../../../../../../modules/dsp/hooks/useDSPLineItemDatasetsQuery";
import { useDSPLineItemTargetData } from "../../../../../../modules/dsp/hooks/useDSPLineItemTargetData";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../../../../utils/formatText";
import moment from "moment";
import { Button } from "../../../../../../components/core/basic/Button";
import DetailedMetric from "../../../../../../components/core/blocks/DetailedMetric";
import LargeValue from "../../../../../../components/core/blocks/LargeValue";
import { formatCurrency } from "../../../../../../utils/formatNumber";
import Loading from "../../../../../../components/core/blocks/Loading";
import { DSPCreativesTable } from "../../../../../../modules/dsp/components/DSPCreativesTable";
import { useAppSelector } from "../../../../../../redux/hooks";
import DSPAudiencesTable from "../../../../../../modules/dsp/components/DSPAudiencesTable";
import DSPInventoryTable from "../../../../../../modules/dsp/components/DSPInventoryTable";
import DSPOverlapDashboard from "../../../../../../modules/dsp/components/DSPOverlapDashboard";
import DSPLineItemForm from "../../../../../../modules/dsp/forms/DSPLineItemForm";
import { useQueryClient } from "@tanstack/react-query";
import useQueryParams from "../../../../../../hooks/useQueryParams";
import DSPLineItemAutomation from "../../../../../../modules/dsp/components/DSPLineItemAutomation";
import LineItemBiddingProgramLog from "../../../../../../components/custom/programs/bidding/LineItemBiddingProgramLog";
import DSPProductsTable from "../../../../../../modules/dsp/components/DSPProductsTable";
import DSPGeoDashboard from "../../../../../../modules/dsp/components/DSPGeoDashboard";
import DSPAudienceGroupsForm from "../../../../../../modules/dsp/forms/DSPAudienceGroupsForm";
import { useDSPLineItemCreativeAssociationData } from "../../../../../../modules/dsp/hooks/useDSPLineItemCreativeAssociationData";
import { Flex, VStack } from "../../../../../../../styled-system/jsx";
import ConfirmationModal from "../../../../../../components/core/blocks/ConfirmationModal";
import { Switch } from "../../../../../../components/fields/base/Switch";
import useDSPSellerInfo from "../../../../../../modules/dsp/hooks/useDSPSellerInfo";
import { Modal } from "../../../../../../components/core/basic/Modal";
import { css } from "../../../../../../../styled-system/css";
import { useSimpleCan } from "../../../../../../config/casl/casl";
import { useDSPCampaignsDatasetsQuery } from "../../../../../../modules/dsp/hooks/useDSPCampaignDatasetsData";
import { getFlightDateRange } from "../../../../../../modules/dsp/utils/flights";

const DEFAULT_TAB = "settings";
const DEFAULT_SETTINGS_TAB = "lineItemSettings";

const Dashboard = () => {
  const { t } = useTranslation(["dsp"]);
  const [selectedTab, setSelectedTab] = useQueryParams("tab", DEFAULT_TAB);
  const [settingsSubTab, setSettingsSubTab] = useQueryParams("subtab", DEFAULT_SETTINGS_TAB);
  const [showLineItemEditModal, setShowLineItemEditModal] = useState(false);
  const [cloneLineItem, setCloneLineItem] = useState(false);
  const [cloneWithCreatives, setCloneWithCreatives] = useState(true);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel);
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");
  const hasDSPModule = useModuleEnabled("DSP");
  const { id: lineItemId } = Route.useParams();
  const { group, start, end } = useDates();
  const longDate = useDateFormatter("long");
  const queryClient = useQueryClient();

  const lineItemIdFilter = { id: "line_item_id", value: { op: "eq", value: lineItemId } };

  const dspTimeseriesQuery = useDSPLineItemDatasetsQuery(`line_item_id,start_date`, false, [lineItemIdFilter]);
  const dspTimeseriesData = dspTimeseriesQuery.data?.data;

  const { dspAdvertiser, isLoading: isSellerInfoLoading } = useDSPSellerInfo();

  const adGroupIdFilter = { id: "ad_group_id", value: { op: "eq", value: lineItemId } };
  const lineItemMetadataQuery = useDSPLineItemDatasetsQuery("line_item_id", true, [adGroupIdFilter]);
  const lineItemMetadata = lineItemMetadataQuery.data?.data;

  const lineItemStatsQuery = useDSPLineItemDatasetsQuery("line_item_id", false, [lineItemIdFilter]);
  const lineItemData = lineItemStatsQuery.data?.data;
  const isLoading = isSellerInfoLoading || lineItemMetadataQuery.isLoading || lineItemStatsQuery.isLoading;

  const lineItemRow = lineItemMetadata?.[0];
  const lineItemEntityId = lineItemRow?.id;
  const { data: targetData } = useDSPLineItemTargetData(lineItemEntityId);

  const { data: creativeAssociationData, isLoading: isCreativeAssocationDataLoading } =
    useDSPLineItemCreativeAssociationData(lineItemId);

  const lineItemDetails = useMemo(() => {
    return { ...lineItemRow, targets: targetData ?? [], creativeAssociations: creativeAssociationData };
  }, [lineItemRow, targetData, creativeAssociationData]);

  const allLineItemsQuery = useDSPLineItemDatasetsQuery("line_item_id", true);
  const allLineItems = allLineItemsQuery.data?.data;

  const dspCampaignsQuery = useDSPCampaignsDatasetsQuery("campaign_id", true);

  const {
    data: dspCreativeStatsData,
    metadata: dspCreativeMetadata,
    isLoading: isDSPCreativeDataLoading,
  } = useDSPCreativeData("creative_id", true, true, { line_item_id: lineItemId });

  const lineItemBreadcrumbOptions = useMemo(() => {
    return allLineItems?.flatMap((lineItem) =>
      lineItem.campaign_id === lineItemRow?.campaign_id ?
        { ...lineItem, href: `/user/dsp/${dspAdvertiser?.id}/lineitems/${lineItem.line_item_id}` }
      : [],
    );
  }, [lineItemRow, allLineItems, dspAdvertiser?.id]);

  const lineItemOverviewData = useMemo(() => {
    return {
      total: lineItemData?.[0] ?? {},
      date: dspTimeseriesData ?? [],
      metadata: lineItemMetadata ?? [],
      isLoading: dspCampaignsQuery.isLoading || isLoading,
    };
  }, [lineItemMetadata, lineItemData, dspTimeseriesData, dspCampaignsQuery.isLoading, isLoading]);

  const secondaryMetricTileData = useMemo(() => {
    if (!lineItemOverviewData.metadata?.length || !dspCampaignsQuery.data?.data?.length) {
      return {};
    }

    const metadata = lineItemOverviewData?.metadata?.[0] ?? {};

    const orderMetadata = dspCampaignsQuery.data?.data?.find((order) => order.campaign_id === lineItemRow?.campaign_id);
    const { startDate: orderStart, endDate: orderEnd } = getFlightDateRange(orderMetadata?.flights ?? []);

    const processedData = {
      state: snakeCaseToSentenceCase(metadata.state ?? ""),
      deliveryStatus: snakeCaseToSentenceCase(metadata.delivery_status ?? ""),
      orderStartDate: orderStart ? longDate.format(new Date(orderStart)) : undefined,
      orderEndDate: orderEnd ? longDate.format(moment(orderEnd).add(1, "day").toDate()) : undefined,
      lineItemStartDate: metadata.start_date_time ? longDate.format(new Date(metadata.start_date_time)) : undefined,
      lineItemEndDate: metadata.end_date_time ? longDate.format(new Date(metadata.end_date_time)) : undefined,
      budget: metadata.budget_amount,
      optimization: snakeCaseToSentenceCase(metadata.optimization?.bidStrategy ?? "")?.replace("Kpi", "KPI"),
      durationInDays: moment(metadata.end_date_time).diff(moment(metadata.start_date_time), "days"),
      daysCompleted: moment()?.diff(moment(metadata.start_date_time), "days"),
    };

    return processedData;
  }, [lineItemOverviewData, lineItemRow, dspCampaignsQuery.data, longDate]);

  const dspCreativesData = useMemo(() => {
    const combinedData = dspCreativeStatsData?.map((stats) => {
      const metadata = dspCreativeMetadata?.find((row) => row.creative_id === stats.creative_id) ?? {};

      return { ...metadata, ...stats };
    });
    return combinedData;
  }, [dspCreativeMetadata, dspCreativeStatsData]);

  const lineItemName = lineItemRow?.name;
  const campaignName = dspCampaignsQuery.data?.data?.find(
    (order) => order.campaign_id === lineItemRow?.campaign_id,
  )?.name;
  const advertiserId = lineItemRow?.advertiser_id;

  // Overview stat tile and chart metrics
  const overviewMetrics = useMemo(() => {
    return [
      getMetricSpec("dsp_sales"),
      getMetricSpec("dsp_total_sales"),
      getMetricSpec("dsp_ecpm"),
      getMetricSpec("dsp_impressions"),
      getMetricSpec("dsp_viewable_impressions"),
      getMetricSpec("dsp_viewability"),
      getMetricSpec("dsp_clicks"),
      getMetricSpec("dsp_cost"),
      getMetricSpec("dsp_total_purchases"),
      getMetricSpec("dsp_units"),
      getMetricSpec("dsp_total_units"),
      getMetricSpec("dsp_ctr"),
      getMetricSpec("dsp_total_acos"),
      getMetricSpec("dsp_total_roas"),
      getMetricSpec("dsp_total_detail_page_views"),
      getMetricSpec("dsp_total_detail_page_view_rate"),
      getMetricSpec("dsp_total_add_to_cart"),
      getMetricSpec("dsp_new_to_brand_detail_page_views"),
      getMetricSpec("dsp_new_to_brand_detail_page_views_rate"),
      getMetricSpec("dsp_total_new_to_brand_dpvs"),
      getMetricSpec("dsp_total_new_to_brand_dpv_rate"),
      getMetricSpec("dsp_brand_searches"),
      getMetricSpec("dsp_brand_searches_rate"),
      getMetricSpec("dsp_avg_base_bid"),
    ];
  }, []);

  const breadCrumbItems = useMemo(() => {
    return [
      {
        name: t(($) => $.dsp["DSP Dashboard"]),
        href: `/user/dsp/${dspAdvertiser?.id}/dashboard`,
        unsorted: true,
        dropdownLinkOptions: DASHBOARD_LINKS,
        dropdownSubtitle: t(($) => $.dsp.Dashboards),
        activeOption: t(($) => $.dsp["DSP Dashboard"]),
      },
      {
        name: campaignName,
        href: `/user/dsp/${dspAdvertiser?.id}/orders/${lineItemRow?.campaign_id}`,
        dropdownLinkOptions: dspCampaignsQuery.data?.data?.map((campaign) => ({
          ...campaign,
          href: `/user/dsp/${dspAdvertiser?.id}/orders/${campaign.campaign_id}`,
        })),
        searchable: true,
        searchPlaceholderText: t(($) => $.dsp["Search DSP orders..."]),
        dropdownSubtitle: t(($) => $.dsp["DSP Orders"]),
        activeOption: campaignName,
      },
      {
        name: lineItemName,
        href: `/user/dsp/${dspAdvertiser?.id}/lineitems/${lineItemId}`,
        dropdownLinkOptions: lineItemBreadcrumbOptions,
        searchable: true,
        searchPlaceholderText: t(($) => $.dsp["Search DSP line items..."]),
        dropdownSubtitle: t(($) => $.dsp["DSP Line Items"]),
      },
    ];
  }, [
    dspAdvertiser,
    lineItemId,
    lineItemName,
    lineItemRow,
    campaignName,
    dspCampaignsQuery.data,
    lineItemBreadcrumbOptions,
    t,
  ]);

  const orderDates = useMemo(() => {
    const orderMetadata = dspCampaignsQuery.data?.data?.find((order) => order.campaign_id === lineItemRow?.campaign_id);
    return getFlightDateRange(orderMetadata?.flights ?? []);
  }, [dspCampaignsQuery.data, lineItemRow]);

  const closeModal = () => {
    setShowLineItemEditModal(false);
    setCloneLineItem(false);
  };

  if (!hasDSPModule || channel.currentChannel !== "amazon") {
    return <Navigate to="/user/dashboard" replace />;
  }

  const { optimization, creation_date_time, name, state, frequencies, delivery_status, targeting_settings, bid } =
    lineItemRow ?? {};

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="dspOverviewDashboard"
            title={lineItemName}
            initialSelectedMetric={["dsp_sales", "dsp_ecpm", "dsp_impressions", "dsp_total_purchases", "dsp_clicks"]}
            totals={{
              data: lineItemOverviewData?.total,
              loading: isLoading,
            }}
            timeline={{
              data: lineItemOverviewData?.date || [],
              loading: isLoading,
            }}
            metrics={overviewMetrics}
            group={group}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {longDate.format(start.toDate())}
                {" - "}
                {longDate.format(end.toDate())}
              </>
            }
          >
            {/* Metadata Stat Tiles */}
            <Row className="pt-5">
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue center name={t(($) => $.dsp.State)} value={secondaryMetricTileData?.state} />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue
                  center
                  name={t(($) => $.dsp["Delivery Status"])}
                  value={secondaryMetricTileData?.deliveryStatus}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue
                  center
                  name={t(($) => $.dsp["Start Date"])}
                  value={secondaryMetricTileData?.lineItemStartDate}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue
                  center
                  name={t(($) => $.dsp["End Date"])}
                  value={secondaryMetricTileData?.lineItemEndDate}
                />
              </Col>
              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <DetailedMetric
                  center
                  metric={{
                    id: "orderBudget",
                    name: t(($) => $.dsp.Budget),
                    format: "currency",
                    decimalPlaces: 0,
                    accessor: (data) => data.value,
                  }}
                  data={{ value: secondaryMetricTileData?.budget }}
                />
              </Col>

              <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                <LargeValue center name={t(($) => $.dsp.Optimization)} value={secondaryMetricTileData?.optimization} />
              </Col>
            </Row>

            <Tabs selectedKey={selectedTab} onSelectionChange={setSelectedTab}>
              <VStack alignItems="stretch" width="100%">
                <TabList>
                  <Tab id="settings">{t(($) => $.dsp.Settings)}</Tab>
                  <Tab id="audiences">{t(($) => $.dsp.Audiences)}</Tab>
                  <Tab id="audienceOpportunities">{t(($) => $.dsp["Audience Overlap"])}</Tab>
                  <Tab id="creatives">{t(($) => $.dsp.Creatives)}</Tab>
                  <Tab id="inventory">{t(($) => $.dsp.Inventory)}</Tab>
                  <Tab id="products">{t(($) => $.dsp.Products)}</Tab>
                  <Tab id="featured_products">{t(($) => $.dsp["Featured Products"])}</Tab>
                  <Tab id="geo">{t(($) => $.dsp.Geography)}</Tab>
                </TabList>

                <TabPanel id="settings">
                  {isLoading ?
                    <Col xs={12}>
                      <Loading height="300px" />
                    </Col>
                  : !lineItemRow ?
                    <Col xs={12}>{t(($) => $.dsp["No Data Available"])}</Col>
                  : <Tabs orientation="vertical" selectedKey={settingsSubTab} onSelectionChange={setSettingsSubTab}>
                      <TabList>
                        <Tab id="lineItemSettings">{t(($) => $.dsp.Summary)}</Tab>
                        <Tab id="automation">{t(($) => $.dsp.Automation)}</Tab>
                        <Tab id="bidding_logs">{t(($) => $.dsp["Bidding Logs"])}</Tab>
                        <Tab id="target_audiences">{t(($) => $.dsp["Target Audiences"])}</Tab>
                      </TabList>

                      <TabPanel id="lineItemSettings">
                        <Col xs={12} className="d-flex justify-content-end align-items center mx-auto">
                          <Modal
                            isOpen={showLineItemEditModal}
                            onOpenChange={(isOpen) => {
                              if (!isOpen) {
                                closeModal();
                              }
                            }}
                            css={{
                              width: "70vw",
                              maxHeight: "95vh",
                              paddingX: "xlarge",
                            }}
                          >
                            <Row className={css({ marginTop: "large" })}>
                              <Col xs={12}>
                                <DSPLineItemForm
                                  isLoading={isLoading || isCreativeAssocationDataLoading}
                                  initialFormData={
                                    cloneLineItem ?
                                      {
                                        ...lineItemDetails,
                                        inventory_type: "DISPLAY",
                                        id: null,
                                        name: `${lineItemDetails.name} (copy)`,
                                        creativeAssociations:
                                          cloneWithCreatives ? lineItemDetails.creativeAssociations : [],
                                        start_date_time:
                                          moment(orderDates?.startDate).isAfter(moment()) ?
                                            moment(orderDates?.startDate).toISOString()
                                          : moment().startOf("hour").add(1, "hour").toISOString(),
                                      }
                                    : (lineItemDetails ?? {})
                                  }
                                  onCancel={() => {
                                    closeModal();
                                  }}
                                  onSuccessfulSubmit={() => {
                                    queryClient.invalidateQueries({
                                      predicate: (query) =>
                                        ["dspLineItemData", "dspLineItemDatasets"].some(
                                          (key) => query.queryKey[0] === key,
                                        ),
                                    });
                                    closeModal();
                                  }}
                                  cloneMode={cloneLineItem}
                                  campaignId={lineItemRow?.campaign_id}
                                  adGroupId={lineItemId}
                                  advertiserId={advertiserId}
                                  orderStartDate={orderDates?.startDate}
                                  orderEndDate={orderDates?.endDate}
                                />
                              </Col>
                            </Row>
                          </Modal>
                          {userCanUpdateDSPEntity && (
                            <Flex gap="medium">
                              <Button
                                onPress={() => {
                                  setShowLineItemEditModal(true);
                                }}
                              >
                                {t(($) => $.dsp["Edit Line Item"])}
                              </Button>
                              <ConfirmationModal
                                title={t(($) => $.dsp["Clone Line Item Details"])}
                                modalButtonText={t(($) => $.dsp["Clone Line Item"])}
                                confirmButtonText={t(($) => $.dsp.Continue)}
                                confirmationMessage={
                                  <Flex direction="column" alignItems={"center"}>
                                    <p>
                                      {t(
                                        ($) =>
                                          $.dsp["Would you like to use the same creatives from the source line item?"],
                                      )}
                                    </p>
                                    <Flex gap={"medium"} alignItems={"center"} padding={"medium"}>
                                      <span>{t(($) => $.dsp.No)}</span>
                                      <Switch
                                        isSelected={cloneWithCreatives}
                                        onChange={() => {
                                          setCloneWithCreatives(!cloneWithCreatives);
                                        }}
                                      />
                                      <span>{t(($) => $.dsp.Yes)}</span>
                                    </Flex>
                                  </Flex>
                                }
                                onConfirm={() => {
                                  setCloneLineItem(true);
                                  setShowLineItemEditModal(true);
                                }}
                              />
                            </Flex>
                          )}
                        </Col>
                        <Col xs={12} lg={6}>
                          <ReadOnlyTable borderless hover>
                            <tbody>
                              <tr>
                                <th>{t(($) => $.dsp.Name)}</th>
                                <td>{name}</td>
                              </tr>
                              <tr>
                                <th>{t(($) => $.dsp.State)}</th>
                                <td>{toTitleCase(state ?? "")}</td>
                              </tr>

                              <tr>
                                <th>{t(($) => $.dsp["Delivery Status"])}</th>
                                <td>{toTitleCase(snakeCaseToSentenceCase(delivery_status ?? ""))}</td>
                              </tr>
                              <tr>
                                <th>{t(($) => $.dsp["Created On"])}</th>
                                <td>{longDate.format(new Date(creation_date_time))}</td>
                              </tr>
                              <tr>
                                <th>{t(($) => $.dsp["Optimization Strategy"])}</th>
                                <td>{snakeCaseToSentenceCase(optimization?.bidStrategy ?? "")}</td>
                              </tr>
                              <tr>
                                <th>{t(($) => $.dsp["Base Bid"])}</th>
                                <td>
                                  {formatCurrency(bid?.baseBid, {
                                    marketPlace: marketPlace.marketPlace,
                                  })}
                                </td>
                              </tr>
                              <tr>
                                <th>{t(($) => $.dsp["Maximum Average CPM"])}</th>
                                <td>
                                  {bid?.maxAverageCPM ?
                                    formatCurrency(bid?.maxAverageCPM, {
                                      marketPlace: marketPlace.marketPlace,
                                    })
                                  : t(($) => $.dsp["Automatically Optimize"])}
                                </td>
                              </tr>
                              {targeting_settings?.userLocation && (
                                <tr>
                                  <th>{t(($) => $.dsp["Targeting User Location"])}</th>
                                  <td>{toTitleCase(targeting_settings?.userLocation)}</td>
                                </tr>
                              )}
                              {targeting_settings?.userLocationSignal && (
                                <tr>
                                  <th>{t(($) => $.dsp["User Location Signals"])}</th>
                                  <td>{snakeCaseToSentenceCase(targeting_settings?.userLocationSignal)}</td>
                                </tr>
                              )}
                              {targeting_settings?.amazonViewability?.viewabilityTier && (
                                <tr>
                                  <th>{t(($) => $.dsp["Amazon Viewability"])}</th>
                                  <td>
                                    {snakeCaseToSentenceCase(targeting_settings?.amazonViewability?.viewabilityTier)}
                                  </td>
                                </tr>
                              )}
                              {targeting_settings?.timeZoneType && (
                                <tr>
                                  <th>{t(($) => $.dsp["Time Zone Type"])}</th>
                                  <td>{toTitleCase(targeting_settings?.timeZoneType)}</td>
                                </tr>
                              )}
                              <tr>
                                <th>{t(($) => $.dsp.Frequencies)}</th>
                                <td>
                                  {Array.isArray(frequencies) &&
                                    frequencies.map((frequency, index) => (
                                      <Fragment key={index}>
                                        <span>{toTitleCase(frequency.frequencyType)}</span>
                                        {frequency.maxImpressions && (
                                          <span>
                                            {t(($) => $.dsp[": no more than {count} impressions a day"], {
                                              count: frequency.maxImpressions,
                                            })}
                                          </span>
                                        )}
                                      </Fragment>
                                    ))}
                                </td>
                              </tr>
                            </tbody>
                          </ReadOnlyTable>
                        </Col>
                      </TabPanel>

                      <TabPanel id="automation">
                        <DSPLineItemAutomation lineItemId={lineItemEntityId} />
                      </TabPanel>

                      <TabPanel id="bidding_logs">
                        <LineItemBiddingProgramLog lineItemId={lineItemEntityId} />
                      </TabPanel>

                      <TabPanel id="target_audiences">
                        <DSPAudienceGroupsForm lineItemId={lineItemEntityId} advertiserId={advertiserId} />
                      </TabPanel>
                    </Tabs>
                  }
                </TabPanel>

                <TabPanel id="audiences">
                  <DSPAudiencesTable
                    adGroupId={lineItemId}
                    totalImpressions={lineItemOverviewData?.total?.impressions}
                  />
                </TabPanel>

                <TabPanel id="audienceOpportunities">
                  <DSPOverlapDashboard lineItemId={lineItemId} />
                  <DSPAudiencesTable
                    adGroupId={lineItemId}
                    untargetedReport={true}
                    totalImpressions={lineItemOverviewData?.total?.impressions}
                  />
                </TabPanel>

                <TabPanel id="creatives">
                  <DSPCreativesTable
                    data={dspCreativesData}
                    isLoading={isDSPCreativeDataLoading}
                    expanderFilters={{ line_item_id: lineItemId }}
                  />
                </TabPanel>

                <TabPanel id="inventory">
                  <DSPInventoryTable adGroupId={lineItemId} />
                </TabPanel>

                <TabPanel id="products">
                  <DSPProductsTable adGroupId={lineItemId} />
                </TabPanel>

                <TabPanel id="featured_products">
                  <DSPProductsTable adGroupId={lineItemId} featuredAsinsFilter={true} />
                </TabPanel>

                <TabPanel id="geo">
                  <DSPGeoDashboard lineItemId={lineItemId} />
                </TabPanel>
              </VStack>
            </Tabs>
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const DSPLineItemDetails = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/dsp/$dspAdvertiser/lineitems/$id")({
  component: DSPLineItemDetails,
  staticData: { application: "Advertising" },
});
